import { memo } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import Box, { BoxProps } from '@mui/material/Box';
//
import BackgroundIllustration from './illustration_background';

import { m } from 'framer-motion';
import { varBounce, varRotate, varSlide } from 'src/components/animate';

// ----------------------------------------------------------------------

function PageNotFoundIllustration({ ...other }: BoxProps) {
  const theme = useTheme();

  const PRIMARY_MAIN = theme.palette.primary.main;

  const PRIMARY_DARK = theme.palette.primary.dark;

  return (
    <Box {...other}>
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 480 360">
        <BackgroundIllustration />

        <linearGradient
          id="SUN"
          x1="64.297"
          x2="111.109"
          y1="95.623"
          y2="142.436"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FFE16A" />
          <stop offset="1" stopColor="#B78103" />
        </linearGradient>

        <linearGradient
          id="MOON"
          x1="64.297"
          x2="111.109"
          y1="95.623"
          y2="142.436"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#C0C0C0" />
        </linearGradient>

        {theme.palette.mode === 'dark' && (
          <g transform="matrix(2, 0, 0, 2, 80, 70)">
            {/** Create circle around the moon */}

            <m.g
              variants={{
                ...varBounce({
                  durationIn: 3,
                }).in,
              }}
            >
              <circle cx="16" cy="16" r="18" fill="#C0C0C0" opacity="0.10" />

              <circle cx="16" cy="16" r="25" fill="#C0C0C0" opacity="0.05" />
            </m.g>
            <m.g variants={varSlide().inLeft}>
              {/** Create background of the moon */}
              <path
                d="M 30 16 C 30 8.3 23.7 2 16 2 C 13.4 2 10.9 2.7 8.7 4.1 C 8.4 4 8.2 4 8 4 C 6.3 4 5 5.3 5 7 C 5 7.1 5 7.2 5 7.3 C 3 9.8 2 12.8 2 16 C 2 19.1 3.1 22.2 5 24.7 C 5.1 26.6 6.8 28.2 8.8 28 C 11 29.3 13.5 30 16 30 C 19.7 30 23.2 28.6 25.8 26 C 28.1 26.1 30 24.3 30 22 C 30 21.3 29.8 20.6 29.4 20 C 29.8 18.7 30 17.3 30 16 Z"
                fill="#E3D283"
              />
              <path
                d="M 30 16 C 30 8.3 23.7 2 16 2 C 13.4 2 10.9 2.7 8.7 4.1 C 8.4 4 8.2 4 8 4 C 6.3 4 5 5.3 5 7 C 5 7.1 5 7.2 5 7.3 C 3 9.8 2 12.8 2 16 C 2 19.1 3.1 22.2 5 24.7 C 5.1 26.6 6.8 28.2 8.8 28 C 11 29.3 13.5 30 16 30 C 19.7 30 23.2 28.6 25.8 26 C 28.1 26.1 30 24.3 30 22 C 30 21.3 29.8 20.6 29.4 20 C 29.8 18.7 30 17.3 30 16 Z M 12.248 23.582 C 11.464 24.337 8.333 24.17 7.373 22.561 C 6.413 20.952 6.871 18.716 8.539 17.432 C 10.207 16.148 12.191 17.028 13.271 18.944 C 14.351 20.86 13.446 22.429 12.248 23.582 Z M 14.345 13.223 C 12.645 13.223 11.345 11.923 11.345 10.223 C 11.345 8.523 12.645 7.223 14.345 7.223 C 16.045 7.223 17.345 8.523 17.345 10.223 C 17.345 11.923 16.045 13.223 14.345 13.223 Z M 22.251 22.55 C 20.051 22.55 18.251 20.75 18.251 18.55 C 18.251 16.35 20.051 14.55 22.251 14.55 C 24.451 14.55 26.251 16.35 26.251 18.55 C 26.251 20.75 24.451 22.55 22.251 22.55 Z"
                fill="#FFED9E"
                opacity="0.8"
              />
            </m.g>
          </g>
        )}

        {theme.palette.mode === 'light' && (
          <g>
            <m.g
              variants={{
                ...varBounce({
                  durationIn: 3,
                }).in,
              }}
            >
              <path
                fill="#FFAB00"
                d="M111.1 141.2c58.7-1 58.6-88.3 0-89.2-58.6 1-58.6 88.3 0 89.2z"
                opacity="0.12"
              />
            </m.g>
            <m.g variants={varSlide().inLeft}>
              <path
                fill="url(#SUN)"
                d="M111.109 119.029c12.927 0 23.406-10.479 23.406-23.406 0-12.926-10.479-23.406-23.406-23.406s-23.406 10.48-23.406 23.406c0 12.927 10.48 23.406 23.406 23.406z"
              />
            </m.g>
          </g>
        )}
        <path
          fill={PRIMARY_MAIN}
          d="M250.358 140.045a7.093 7.093 0 00-7.093-7.092c-.381.007-.761.04-1.138.097a9.552 9.552 0 00-8.425-5.026h-.343a11.348 11.348 0 10-22.012 0h-.342a9.564 9.564 0 100 19.114h33.177v-.061a7.107 7.107 0 006.176-7.032z"
          opacity="0.14"
        />

        <path
          fill={PRIMARY_MAIN}
          d="M425.621 150.222a8.267 7.093 0 00-7.093-7.092c-.381.007-.761.04-1.138.097a9.552 9.552 0 00-8.425-5.026h-.343a11.348 11.348 0 10-22.012 0h-.342a9.564 9.564 0 100 19.114h33.177v-.061a7.107 7.107 0 006.176-7.032z"
          opacity="0.20"
        />

        <path
          fill={PRIMARY_MAIN}
          d="M104.258 199.045a7.093 7.093 0 00-7.093-7.092c-.381.007-.761.04-1.138.097a9.552 9.552 0 00-8.425-5.026h-.343a11.348 11.348 0 10-22.012 0h-.342a9.564 9.564 0 100 19.114h33.177v-.061a7.107 7.107 0 006.176-7.032z"
          opacity="0.24"
        />

        {/* Birds */}
        {theme.palette.mode === 'light' && (
          <m.g
            variants={
              varSlide({
                durationIn: 2,
              }).inRight
            }
          >
            <path
              style={{
                fill: 'none',
                stroke: PRIMARY_MAIN,
                strokeMiterlimit: 10,
              }}
              d="M265.694,90.983c0,0,1.02-3.061,6.122-4.082    c5.102-1.02,11.225,4.592,11.225,4.592s4.082-8.163,8.163-8.674c4.082-0.51,8.674,3.061,8.674,3.061"
            />
            <path
              style={{
                //   "fill:none;stroke:#263238;stroke-miterlimit:10;"
                fill: 'none',
                stroke: PRIMARY_MAIN,
                strokeMiterlimit: 10,
              }}
              d="M250.694,115.983c0,0,1.02-3.061,6.122-4.082    c-4.898-1.02,11.225,4.592,11.225,4.592s4.082-8.163,8.163-8.674c4.082-0.51,8.674,3.061,8.674,3.061"
            />
            <path
              style={{
                //   "fill:none;stroke:#263238;stroke-miterlimit:10;"
                fill: 'none',
                stroke: PRIMARY_MAIN,
                strokeMiterlimit: 10,
              }}
              d="M300.694,118.983c0,0,1.02-3.061,6.122-4.082    c3.884-0.777,8.544,3.495,8.544,3.495s3.107-6.214,6.214-6.602c3.107-0.388,6.602,2.33,6.602,2.33"
            />
            <path
              style={{
                //   "fill:none;stroke:#263238;stroke-miterlimit:10;"
                fill: 'none',
                stroke: PRIMARY_MAIN,
                strokeMiterlimit: 10,
              }}
              d="M340.694,140.983c0,0,1.02-3.061,6.122-4.082    c2.547-0.509,5.603,2.292,5.603,2.292s2.038-4.075,4.075-4.33c2.037-0.255,4.33,1.528,4.33,1.528"
            />
          </m.g>
        )}

        {/** Stars */}
        {theme.palette.mode === 'dark' && (
          <m.g
            variants={
              varSlide({
                durationIn: 2,
              }).inDown
            }
          >
            <circle cx="50" cy="90" r="1" fill="#FFFFFF" opacity="0.60" />
            <circle cx="50" cy="140" r="1" fill="#FFFFFF" opacity="0.40" />
            <circle cx="80" cy="70" r="1" fill="#FFFFFF" opacity="0.70" />
            <circle cx="120" cy="120" r="1" fill="#FFFFFF" opacity="0.50" />
            <circle cx="150" cy="60" r="1" fill="#FFFFFF" opacity="0.40" />
            <circle cx="180" cy="120" r="1" fill="#FFFFFF" opacity="0.60" />
            <circle cx="210" cy="80" r="1" fill="#FFFFFF" opacity="0.80" />
            <circle cx="240" cy="140" r="1" fill="#FFFFFF" opacity="0.60" />
            <circle cx="270" cy="90" r="1" fill="#FFFFFF" opacity="0.30" />
            <circle cx="300" cy="110" r="1" fill="#FFFFFF" opacity="0.60" />
            <circle cx="330" cy="70" r="1" fill="#FFFFFF" opacity="0.50" />
            <circle cx="360" cy="100" r="1" fill="#FFFFFF" opacity="0.30" />
            <circle cx="390" cy="60" r="1" fill="#FFFFFF" opacity="0.80" />
            <circle cx="420" cy="80" r="1" fill="#FFFFFF" opacity="0.50" />
            <circle cx="420" cy="140" r="1" fill="#FFFFFF" opacity="0.60" />
          </m.g>
        )}
        {/* 0 */}
        <m.g variants={varBounce({ durationIn: 3 }).in}>
          <path
            fill={PRIMARY_DARK}
            d="M244.878 181.46c34.559 0 62.575 28.016 62.575 62.576 0 34.559-28.016 62.575-62.575 62.575-34.56 0-62.576-28.016-62.576-62.575 0-34.56 28.016-62.576 62.576-62.576zm0 23.186c-21.754 0-39.389 17.635-39.389 39.39 0 21.754 17.635 39.389 39.389 39.389s39.389-17.635 39.389-39.389c0-21.755-17.635-39.39-39.389-39.39z"
          />
        </m.g>

        {/* 44 */}
        <m.g variants={varRotate({ durationIn: 1 }).in}>
          <path
            fill={PRIMARY_MAIN}
            d="M174.965 264.592c0-4.133-1.492-5.625-5.637-5.625h-11.373v-66.611c0-4.476-1.492-5.637-5.638-5.637h-9.172a9.867 9.867 0 00-7.948 3.974l-55.03 68.274a11.006 11.006 0 00-1.957 6.787v5.968c0 4.145 1.492 5.637 5.625 5.637h54.676v21.707c0 4.133 1.492 5.625 5.625 5.625h8.12c4.146 0 5.638-1.492 5.638-5.625v-21.707h11.434c4.414 0 5.637-1.492 5.637-5.637v-7.13zm-72.42-5.625l35.966-44.415v44.415h-35.966zM411.607 264.592c0-4.133-1.492-5.625-5.638-5.625h-11.421v-66.611c0-4.476-1.492-5.637-5.638-5.637h-9.11a9.869 9.869 0 00-7.949 3.974l-55.03 68.274a10.998 10.998 0 00-1.981 6.787v5.968c0 4.145 1.491 5.637 5.625 5.637h54.688v21.707c0 4.133 1.491 5.625 5.625 5.625h8.12c4.145 0 5.637-1.492 5.637-5.625v-21.707h11.434c4.476 0 5.638-1.492 5.638-5.637v-7.13zm-72.42-5.625l35.966-44.415v44.415h-35.966z"
          />
        </m.g>
      </svg>
    </Box>
  );
}

export default memo(PageNotFoundIllustration);
